<template lang="pug">
.rogue-app-notifications(:class="{'centered': loading}")
  .notifications-header(v-if="items.length")
    a(@click.stop.prevent="markAllAsRead") Mark All As Read
    a(@click.stop.prevent="deleteAll") Delete All
  NativeAppSpinnerWrapper(:active="loading && !fetching" v-if="loading || items.length !== 0")
    .notifications-items.rogue-scroll-y(v-if="items.length")
      NotificationItem(
        v-show="!loading"
        v-for="item in items"
        :key="item._id"
        :item="item"
        @open="markAsRead($event)"
      )
      RogueButton.load-more(
        v-if="!loading && items.length < totalCount"
        id="btn-load-more"
        width="full"
        :loading="fetching"
        @click="loadMore"
        text="Load More"
      )
  NativeAppPlaceholder(
    v-if="!loading && items.length === 0"
    :media-config="{ src: '/Rogue%20App%20Dashboard/Inbox/email_vknkyb.svg', width: 98, height: 60 }"
  )
    p.placeholder-text You don't have any notifications
</template>

<script setup lang="ts">
import { messages }             from 'Src/js/app-bridge/messages'
import { computed, onMounted, ref, watch } from 'vue'
import NativeAppSpinnerWrapper  from './components/native-app-spinner-wrapper.vue'
import NativeAppPlaceholder     from './components/native-app-placeholder.vue'
import NotificationItem         from './components/native-app-notification-item.vue'
import type { INotificationResponse, IPushNotification } from './notificationTypes'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'

const loading = ref<boolean>(true)
const fetching = ref<boolean>(false)
const items = ref<IPushNotification[]>([])
const totalCount = ref<number | null>(null)
const currentPage = ref(1)
const unreadCount = ref<number>(0)

const uiStore = useUIStore()
const rogue = useRogue()

const lastContentReloadAt = computed<number>(() => uiStore.appLastContentReloadAt || Date.now())

function refreshData() {
  uiStore.appLastContentReloadAt = Date.now()
}

function reset() {
  items.value = []
  totalCount.value = null
  currentPage.value = 1
  unreadCount.value = 0
}

async function load() {
  loading.value = !fetching.value
  const { deviceUid } = rogue.appContext!
  const resp = await rogue.fetch<INotificationResponse>(`/svc_push/notification/${deviceUid}?page=${currentPage.value}&limit=20`)
  items.value = [...items.value, ...resp.notifications]
  totalCount.value = resp.totalCount
  unreadCount.value = resp.unreadCount
  loading.value = false
}

async function loadMore() {
  fetching.value = true
  currentPage.value += 1
  await load()
  fetching.value = false
}

function markAsRead(item: any) {
  rogue.fetch(`/svc_push/notification/read/${item._id}`, 'POST')
  unreadCount.value -= 1
  item.read = true
}

async function markAllAsRead() {
  try {
    const { deviceUid } = rogue.appContext!
    await rogue.fetch(`/svc_push/notification/read/all/${deviceUid}`, 'POST')
    items.value.forEach(item => item.read = true)
    unreadCount.value = 0
  } catch (e) {
    rogue.log.error('Unable to mark as read Rogue App notifications', e)
  }
}

async function deleteAll() {
  try {
    const { deviceUid } = rogue.appContext!
    await rogue.fetch(`/svc_push/notification/${deviceUid}`, 'DELETE')
    reset()
  } catch (e) {
    rogue.log.error('Unable to delete Rogue App notifications', e)
  }
}

watch(lastContentReloadAt, async (curr, prev) => {
  if (curr > prev) {
    reset()
    await load()
    loading.value = false
  }
})

watch(unreadCount, (value) => {
  if (value >= 0) {
    // Send to the app the amount of unread messages
    rogue.emitToApp(messages.UNREAD_UPDATED, { total: value })
  }
})

onMounted(async () => {
  // Initialize the value and trigger `load`
  await refreshData()
})
</script>

<style scoped lang="scss">
.rogue-app-notifications, .notifications-header, .notifications-items {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
}
.rogue-app-notifications.centered {
  display: flex;
  align-items: center;
  justify-content: center;
}
.notifications-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 $space-xs;
  background: $color-black;

  a {
    line-height: $app-button-height;
    text-decoration: none;

    &, &:active, &:hover {
      color: $color-gray-dark;
    }
  }
}
.rogue-app-notifications, .notifications-items {
  bottom: 0;
}
.notifications-header {
  z-index: $z-index-sticky;
}
.notifications-items {
  top: $app-button-height;
  display: flex;
  flex-direction: column;
  gap: $space-xxxs;
  padding: $space-s;
}
.placeholder {
  height: 100%;

  :deep(.placeholder-icon) {
    height: 60px;
    width: 98px;

    img {
      max-height: 60px;
    }
  }
}
</style>
